import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { PartnersUtilsService } from '@app/modules/partners/partners-utils.service';
import { Partner, PaymentType } from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { TranslateModule } from '@ngx-translate/core';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ValidationErrorComponent } from '@shared/components/validation-error/validation-error.component';
import { InputValidationStatusDirective } from '@shared/directives/input-validation-status.directive';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { controlContainerProvider } from '@shared/helpers';
import { Observable, of } from 'rxjs';
import { first, map, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-service-data-form-payment-information',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ValidationErrorComponent,
    InputValidationStatusDirective,
    RequirePermissionDirective,
    AsyncPipe,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: './service-data-form-payment-information.component.html',
  viewProviders: [controlContainerProvider],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDataFormPaymentInformationComponent implements OnInit {
  public id = input<string>(null);

  private controlContainer = inject(ControlContainer);
  private partnersUtilsService = inject(PartnersUtilsService);
  private authService = inject(AccountUtilsService);

  protected readonly Object = Object;
  protected readonly PaymentType = PaymentType;

  private destroyRef = inject(DestroyRef);

  protected paymentTypes$: Observable<string[]>;

  protected get form() {
    return this.controlContainer.control as FormGroup;
  }

  ngOnInit(): void {
    this.form
      .get('paymentType')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((paymentType: PaymentType) => {
        if (
          paymentType !== PaymentType.Customer1 &&
          paymentType !== PaymentType.Customer2
        ) {
          this.form.get('notes').reset('');
        }
      });

    this.paymentTypes$ = this.getPaymentTypes();
    this.authService
      .hasRequiredPermission$('serviceData.edit.canEditInvoiceNumbers')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((canEditInvoiceNumbersPermission) => {
        if (!canEditInvoiceNumbersPermission) {
          this.form.get('laboratoryInvoiceNumber').disable();
          this.form.get('doctorInvoiceNumber').disable();
        }
      });
  }

  private getPaymentTypes(): Observable<string[]> {
    return this.form.root.get('partnerId').valueChanges.pipe(
      startWith(this.form.root.get('partnerId').value),
      takeUntilDestroyed(this.destroyRef),
      switchMap((partnerId: string) =>
        partnerId
          ? this.partnersUtilsService.getPartnerById$(partnerId)
          : of(null)
      ),
      switchMap((partner: Partner) =>
        partner
          ? this.authService
              .hasRequiredPermission$([
                'serviceData.edit.canSelectPartnerPaymentTypes',
              ])
              .pipe(first())
              .pipe(
                switchMap((canSelectPartnerPaymentTypes: boolean) =>
                  this.authService
                    .hasRequiredPermission$([
                      'serviceData.edit.canSelectAnyPaymentType',
                    ])
                    .pipe(first())
                    .pipe(
                      map((canSelectAnyPaymentType) => {
                        this.form.get('paymentType').enable();

                        if (canSelectAnyPaymentType) {
                          return Object.keys(PaymentType);
                        } else if (canSelectPartnerPaymentTypes) {
                          return partner.paymentTypes;
                        } else {
                          this.form.get('paymentType').disable();
                          return [];
                        }
                      })
                    )
                )
              )
          : of([]).pipe(tap(() => this.form.get('paymentType').disable()))
      )
    );
  }
}
